import { useCallback, useMemo, useState } from "react";
import { useCardDetailsErrorsStore } from "./useCardDetailsErrorsStore";
import { useTranslation } from "@/hooks/useTranslation";
import { useDonationFormStore } from "@/stores";
import { useValidationControl } from "@/hooks/useValidationControl";
import type {
  HostedFields,
  BraintreeError,
  HostedFieldsEvent,
} from "braintree-web";
import { useBraintree } from "@/hooks/useBraintree";
import { trackError } from "@/utils/error-monitoring";

type PaymentErrors = "number" | "expirationDate" | "cvv";
type ValidateResponse = {
  number?: string | null;
  expirationDate?: string | null;
  cvv?: string | null;
} | null;

const errorTypes = ["CUSTOMER", "MERCHANT", "NETWORK", "INTERNAL", "UNKNOWN"];

export function isBraintreeError(e: unknown): e is BraintreeError {
  const error = e as BraintreeError;

  if (!error?.type) return false;
  return errorTypes.includes(error.type) && error instanceof Error;
}

export function useCardDetailsValidation() {
  const { enabled } = useValidationControl();

  const t = useTranslation("card_details");

  const setErrors = useCardDetailsErrorsStore((state) => state.setErrors);
  const resetErrors = useCardDetailsErrorsStore((state) => state.resetErrors);
  const setIsValidated = useCardDetailsErrorsStore(
    (state) => state.setIsValidated,
  );
  const [validFields, setValidFields] = useState<
    Record<PaymentErrors, boolean>
  >({
    number: false,
    expirationDate: false,
    cvv: false,
  });

  const { setNonce, donationAmount } = useDonationFormStore();

  const errorMessages = useMemo(() => {
    return {
      number: t("errors.card-number") as string,
      expirationDate: t("errors.expiration-date") as string,
      cvv: t("errors.cvv") as string,
    };
  }, [t]);

  const { threeDSecure } = useBraintree();

  const validate = useCallback(
    async (instance?: HostedFields): Promise<ValidateResponse> => {
      setIsValidated(true);
      if (!enabled) {
        return null;
      }

      if (!instance) {
        setErrors(errorMessages);
        return errorMessages;
      }

      try {
        const { nonce, details } = await instance.tokenize();

        if (!threeDSecure) {
          setNonce(nonce);
          resetErrors();
          return null;
        }

        const verifyResult = await threeDSecure.verifyCard({
          nonce,
          amount: `${donationAmount ?? 0}`,
          bin: details.bin,
        });

        if (!verifyResult.threeDSecureInfo?.liabilityShifted) {
          const errors = {
            number: t("errors.invalid-card") as string,
          };
          setErrors(errors);
          setValidFields((prev) => ({ ...prev, number: false }));
          return errors;
        }

        setNonce(verifyResult.nonce);
        resetErrors();
        return null;
      } catch (error) {
        if (isBraintreeError(error)) {
          switch (error.code) {
            case "HOSTED_FIELDS_FIELDS_EMPTY": {
              const errors = {
                number: t("errors.card-number") as string,
                expirationDate: t("errors.expiration-date") as string,
                cvv: t("errors.cvv") as string,
              };
              setErrors(errors);
              setValidFields({
                number: false,
                expirationDate: false,
                cvv: false,
              });
              return errors;
            }

            case "HOSTED_FIELDS_FIELDS_INVALID": {
              const fields = error.details.invalidFieldKeys as PaymentErrors[];
              const fieldErrors = fields.reduce(
                (acc, field) => {
                  const message = errorMessages[field];
                  if (message) {
                    acc[field] = message;
                  }
                  return acc;
                },
                {} as Record<PaymentErrors, string | null>,
              );

              setValidFields((prev) => {
                const updated = { ...prev };
                fields.forEach((field) => {
                  updated[field] = false;
                });
                return updated;
              });
              setErrors(fieldErrors);
              return fieldErrors;
            }

            case "HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR": {
              const errors = {
                number: t("errors.network-error") as string,
              };
              setErrors(errors);
              setValidFields((prev) => ({ ...prev, number: false }));
              return errors;
            }

            default: {
              trackError(error as Error);
              const errors = {
                number: t("errors.invalid-card") as string,
              };
              setErrors(errors);
              setValidFields((prev) => ({ ...prev, number: false }));
              return errors;
            }
          }
        } else {
          const errors = {
            number: t("errors.card-number") as string,
          };
          setValidFields((prev) => ({ ...prev, number: false }));
          setErrors(errors);
          return errors;
        }
      }
    },
    [
      enabled,
      setErrors,
      setNonce,
      resetErrors,
      t,
      setIsValidated,
      errorMessages,
      donationAmount,
      threeDSecure,
    ],
  );

  const validateField = useCallback(
    (event: HostedFieldsEvent): string | null => {
      if (!enabled) {
        return null;
      }

      const { isValid, isEmpty } = event.fields[event.emittedBy];
      const field = event.emittedBy as PaymentErrors;

      const message = !isValid && !isEmpty ? errorMessages[field] : null;
      setValidFields((prev) => ({
        ...prev,
        [field]: isValid,
      }));
      setErrors({ [field]: message });
      return message;
    },
    [enabled, setErrors, errorMessages],
  );

  return {
    validateField,
    validFields,
    validate,
  };
}
